import * as React from 'react'

import { cn } from '../lib/utils'
import { Avatar } from './avatar'
import { Tooltip } from './tooltip'

interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  people: {
    name: string
    email?: string
    phone?: string
    photo?: string
    online?: boolean
    id?: string
  }[]
  max?: number
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  showTooltip?: boolean
  dataTestID?: string
  variant?: 'basic' | 'square'
  badgeColor?: string
  badgeBoxSize?: string
  useUserAvatar?: boolean
  UserAvatarComponent?: React.ComponentType<{
    user: {
      email?: string
      id?: string
      name?: string
      photo?: string | null
      online?: boolean
      phone?: string
    }
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
    showTooltip?: boolean
    variant?: 'basic' | 'square'
  }>
}

const badgeSizeMap = {
  xs: '0.5rem',
  sm: '0.625rem',
  md: '0.75rem',
  lg: '0.875rem',
  xl: '1rem',
  '2xl': '1.25rem'
}

const OnlineBadge = ({
  boxSize = '1em',
  color = 'bg-green-500',
  size
}: {
  boxSize?: string
  color?: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
}) => (
  <span
    className={cn(
      'absolute rounded-full border-2 border-background dark:border-gray-800',
      color,
      'right-0 bottom-0 -translate-y-[15%] translate-x-[15%]'
    )}
    style={{
      width: boxSize || badgeSizeMap[size || 'md'],
      height: boxSize || badgeSizeMap[size || 'md']
    }}
  />
)

const AvatarWithFallback = ({
  name,
  src,
  variant,
  size,
  className,
  dataTestID
}: {
  name: string
  src?: string
  variant?: 'basic' | 'square'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  className?: string
  dataTestID?: string
}) => {
  const [hasError, setHasError] = React.useState(false)

  return (
    <Avatar
      data-testid={dataTestID}
      variant={variant}
      name={name}
      src={hasError ? undefined : src}
      size={size}
      className={className}
      onError={() => setHasError(true)}
    />
  )
}

const TooltipAvatar = ({
  name,
  src,
  variant,
  online,
  badgeColor,
  badgeBoxSize,
  size,
  className,
  dataTestID
}: {
  name: string
  src?: string
  variant?: 'basic' | 'square'
  online?: boolean
  badgeColor?: string
  badgeBoxSize?: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  className?: string
  dataTestID?: string
}) => (
  <Tooltip content={<span className="text-sm">{name}</span>} side="bottom">
    <div className={cn('relative inline-block', className)}>
      <AvatarWithFallback
        name={name}
        src={src}
        variant={variant}
        size={size}
        className="border-2 border-white dark:border-gray-800"
        dataTestID={dataTestID}
      />
      {online && <OnlineBadge color={badgeColor} boxSize={badgeBoxSize} size={size} />}
    </div>
  </Tooltip>
)

export const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(
  (
    {
      people,
      max = 5,
      size = 'md',
      showTooltip = false,
      dataTestID,
      variant = 'basic',
      badgeColor = 'bg-green-500',
      badgeBoxSize,
      useUserAvatar = false,
      UserAvatarComponent,
      className,
      ...props
    },
    ref
  ) => {
    const displayPeople = people?.slice(0, max)
    const remainingCount = (people?.length || 0) - max

    return (
      <div ref={ref} className={cn('flex -space-x-2 rtl:space-x-reverse', className)} {...props}>
        {displayPeople?.map((user, index) => {
          const userName = user.name || user.email || user.phone || 'Unknown User'

          // Render UserAvatar component if useUserAvatar is true and component is provided
          if (useUserAvatar && UserAvatarComponent) {
            return (
              <div key={index} className="relative inline-block hover:z-10 transition-transform hover:-translate-y-1">
                <UserAvatarComponent
                  user={{
                    email: user.email,
                    id: user.id,
                    name: user.name,
                    photo: user.photo,
                    online: user.online,
                    phone: user.phone
                  }}
                  size={size}
                  showTooltip={showTooltip}
                  variant={variant}
                />
              </div>
            )
          }

          if (!showTooltip) {
            return (
              <div key={index} className="relative inline-block hover:z-10 transition-transform hover:-translate-y-1">
                <AvatarWithFallback
                  dataTestID={dataTestID}
                  variant={variant}
                  name={userName}
                  src={user.photo}
                  size={size}
                  className="border-2 border-white dark:border-gray-800"
                />
                {user.online && <OnlineBadge color={badgeColor} boxSize={badgeBoxSize} size={size} />}
              </div>
            )
          }

          return (
            <TooltipAvatar
              key={index}
              name={userName}
              src={user.photo}
              variant={variant}
              online={user.online}
              badgeColor={badgeColor}
              badgeBoxSize={badgeBoxSize}
              size={size}
              dataTestID={dataTestID}
              className="relative inline-block hover:z-10 transition-transform hover:-translate-y-1"
            />
          )
        })}

        {remainingCount > 0 && (
          <Tooltip content={<span className="text-sm">View {remainingCount} more</span>} side="bottom">
            <div className="relative inline-block hover:z-10">
              <Avatar
                variant={variant}
                name={`+${remainingCount}`}
                size={size}
                className={cn(
                  'border-2 border-white dark:border-gray-800',
                  'bg-gray-900 text-white hover:bg-gray-700',
                  'dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200',
                  'transition-colors'
                )}
              />
            </div>
          </Tooltip>
        )}
      </div>
    )
  }
)

AvatarGroup.displayName = 'AvatarGroup'
